import * as React from 'react';
import assign from 'object-assign';
import getEventDurationDisplayString from 'mini-common-utils/lib/utils/getEventDurationDisplayString';
import StartTimeAndEndTime from 'mini-rsvp/lib/components/StartTimeAndEndTime';
import strings from 'owa-strings/owa-mini-common-strings';
import { differenceInCalendarDays, formatUserTime, isEqual } from 'owa-datetime';
import { format } from 'owa-strings';
import { getAriaLabelForTimeAndDuration } from 'mini-common-utils/lib/utils/getAriaLabelForTimeAndDuration';
import { getRelativeDayString } from 'mini-common-utils/lib/utils/getRelativeDayString';
import { HubItem } from 'mini-hub';
import { MiniIcons } from 'owa-mini-icons';
import { TimeAndDurationProps } from '../schema/TimeAndDuration.types';
import {
    getDateDayMonthYearLong,
    getDateDayMonthYearShort,
} from 'mini-common-utils/lib/utils/calendarDateFormatUtils';

const timeAndDurationStyles = require('./TimeAndDuration.scss');

/**
 * Renders the time and duration of a calendar event
 */
export default function TimeAndDuration(props: TimeAndDurationProps) {
    const { styles: propStyles } = props;
    const styles = assign({}, timeAndDurationStyles, propStyles);
    /* TODO: Set recurrence string as subtitle. */
    return (
        <HubItem
            icon={MiniIcons.Clock}
            onRenderText={() => renderTimeAndDuration(props, true /**isInHubItem */)}
            styles={{ className: styles.itemContainer }}
            ariaLabel={getAriaLabelForTimeAndDuration(props.item)}
        />
    );
}

export function renderTimeAndDuration(
    props: TimeAndDurationProps,
    isInHubItem: boolean = false
): JSX.Element {
    const { Start, End, IsEffectiveAllDayEvent } = props.item;
    const { styles: propStyles } = props;
    const styles = assign({}, timeAndDurationStyles, propStyles);

    let timeAndDuration: JSX.Element;
    const isOneDayAllDayEvent =
        IsEffectiveAllDayEvent && differenceInCalendarDays(End, Start) === 1;
    const isOneDayTimedEvent =
        !IsEffectiveAllDayEvent && differenceInCalendarDays(End, Start) === 0;
    // Don't show duration for 0 minute events
    const durationString = !isEqual(Start, End) && getEventDurationDisplayString(Start, End);

    const getStartDateString = (short: boolean) => {
        const startDateRelativeDay = getRelativeDayString(Start);
        const formattedStartDate = short
            ? getDateDayMonthYearShort(Start)
            : getDateDayMonthYearLong(Start);
        return startDateRelativeDay
            ? format(
                  strings.relativeDayPrefixForDateFormat,
                  startDateRelativeDay,
                  formattedStartDate
              )
            : formattedStartDate;
    };

    if (isOneDayAllDayEvent || isOneDayTimedEvent) {
        const dateString = getStartDateString(false);
        timeAndDuration = (
            <>
                <span className={styles.firstLine} aria-label={dateString} tabIndex={0}>
                    {dateString}
                </span>
                <div className={styles.secondLine}>
                    {isOneDayAllDayEvent ? (
                        <span aria-hidden>{strings.allDayMeetingLabel}</span>
                    ) : (
                        <div className={styles.timingAndDuration}>
                            <StartTimeAndEndTime start={Start} end={End} />
                            {durationString && (
                                <span className={styles.duration} aria-hidden>
                                    ({durationString})
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </>
        );
    } else {
        const startDateString = IsEffectiveAllDayEvent
            ? getStartDateString(true)
            : format(
                  strings.dateFormatWithTimeFormat,
                  getStartDateString(true),
                  formatUserTime(Start)
              );
        const endDateString = IsEffectiveAllDayEvent
            ? getDateDayMonthYearShort(End)
            : format(
                  strings.dateFormatWithTimeFormat,
                  getDateDayMonthYearShort(End),
                  formatUserTime(End)
              );
        timeAndDuration = (
            <>
                <div className={styles.firstLine}>
                    <label className={styles.startLabel}>{strings.startOfMeetingLabel}</label>{' '}
                    {startDateString}
                </div>
                <div className={styles.secondLine}>
                    <label className={styles.endLabel}>{strings.endOfMeetingLabel}</label>{' '}
                    {endDateString}
                    <span className={styles.duration} aria-hidden>
                        ({durationString})
                    </span>
                </div>
            </>
        );
    }

    const tabIndexProps = !isInHubItem
        ? {
              tabIndex: 0,
              'aria-label': `${getAriaLabelForTimeAndDuration(props.item)}${getStartDateString(
                  false
              )}`,
          }
        : {};
    return <div {...tabIndexProps}>{timeAndDuration}</div>;
}
